<template>
    <div v-if="video.settings.status === 0" class="card px-3 py-4 w-100">
        <h1 class="m-auto">
            {{ $t('lesson.live.unavailable') }}
        </h1>
        <h3 class="mx-auto" v-if="video.settings.start_date">
            {{ $t('lesson.live.start_at') }}: {{ $moment(video.settings.start_date).format('LLL') }}
        </h3>
    </div>
    <div v-else-if="video && url" class="video-player-container">
        <Player :video="url" :contentId="contentId" @info="handlePlayerInfo"/>
    </div>
</template>

<script>
import Player from '../../Common/Player.vue';
import { NCH_ENV } from '../../../../../js/nch-env';

export default {
    emits: ['input', 'progress', 'update:modelValue'],
    name: 'EVT',
    components: {
        Player,
    },

    props: {
        modelValue: Object,
        contentId: {
            type: Number,
            required: true,
        },
        lessonId: {
            type: Number,
            required: true,
        },
    },

    data() {
        return {
            isTracking: false,
            watchTimeSeconds: 0,
            trackingInterval: null,
            lastPingTime: Date.now(),
            isPlaying: false,
            pingInterval: null,
            isPageVisible: !document.hidden,
            playerReadyNotified: false,
            isMountedComponent: false,
        };
    },

    computed: {
        user() {
            const currentUser = this.$store.getters.USER;
            return currentUser;
        },
        url() {
            if (!this.video?.settings?.live_data?.shareLink) {
                console.warn('[ContentEvt] shareLink não encontrado:', {
                    video: this.video,
                    settings: this.video?.settings,
                    live_data: this.video?.settings?.live_data,
                });
                return null;
            }
            const shareLink = this.video.settings.live_data.shareLink;
            const startIndex = shareLink.indexOf('evt');
            if (startIndex === -1) {
                console.warn('[ContentEvt] "evt" não encontrado no shareLink:', shareLink);
                return null;
            }
            const generatedUrl = shareLink.substring(startIndex);
            return generatedUrl;
        },
        video: {
            get() {
                return this.modelValue;
            },
            set(newValue) {
                this.$emit('update:modelValue', newValue);
                this.$emit('input', newValue);
            },
        },
    },

    methods: {
        subscribe() {
            if (this.video?.settings?.id) {
                window.Echo
                    .private(`${NCH_ENV.APP_HOST}.Lesson.Live.${this.video.settings.id}`)
                    .listen('LiveLessonEvent', (e) => {
                        if (e.settings) {
                            this.$emit('update:modelValue', {
                                ...this.video,
                                settings: { ...this.video.settings, ...e.settings },
                            });
                        }
                    });
            } else {
                console.warn('[ContentEvt] Não foi possível inscrever-se: video.settings.id ausente.');
            }
        },

        handlePlayerInfo(playerEventData) {
            if (playerEventData.eventName === 'jmvplayerout-status') {
                if (!this.playerReadyNotified) {
                    this.onActualPlayerReady();
                    this.playerReadyNotified = true;
                    this.isPlaying = true;
                }
                this.onActualTimeUpdate();
            }

            if (playerEventData.eventName === 'jmvplayerout-play') {
                this.onActualPlay();
            }

            if (playerEventData.eventName === 'jmvplayerout-pause') {
                this.onActualPause();
            }
        },

        onActualPlayerReady() {
            this.startTracking();
        },

        onActualTimeUpdate() {
            // A lógica de isPlaying é tratada no primeiro status ou por eventos play/pause.
        },

        onActualPlay() {
            this.isPlaying = true;
        },

        onActualPause() {
            this.isPlaying = false;
        },

        handleVisibilityChange() {
            this.isPageVisible = !document.hidden;
            if (!this.isPageVisible && this.isPlaying) {
                // Página oculta durante reprodução - comportamento pode ser adicionado aqui
            }
        },

        startTracking() {
            if (this.trackingInterval) {
                clearInterval(this.trackingInterval);
            }
            if (this.pingInterval) {
                clearInterval(this.pingInterval);
            }

            this.isTracking = true;
            this.lastPingTime = Date.now();

            this.trackingInterval = setInterval(() => {
                if (this.isPlaying && this.isPageVisible) {
                    this.watchTimeSeconds += 1;
                }
            }, 1000);

            this.pingInterval = setInterval(() => {
                this.sendWatchTime();
            }, 30000);
        },

        stopTracking() {
            if (this.trackingInterval) {
                clearInterval(this.trackingInterval);
                this.trackingInterval = null;
            }
            if (this.pingInterval) {
                clearInterval(this.pingInterval);
                this.pingInterval = null;
            }

            if (this.isTracking) {
                this.sendWatchTime();
            }
            this.isTracking = false;
            this.isPlaying = false;
        },

        async sendWatchTime() {
            const totalWatchedTime = this.watchTimeSeconds;

            try {
                const response = await window.axios.post(`/api/evt-progress/watch-time/${this.lessonId}`, {
                    watch_time: totalWatchedTime,
                });

                if (response.data && response.data.data) {
                    const progressData = response.data.data;
                    this.$emit('update:modelValue', {
                        ...this.video,
                        progress: progressData.progress_percentage,
                        time: progressData.watch_time_seconds,
                    });

                    this.$emit('progress', {
                        time: progressData.watch_time_seconds,
                        percentageWatched: progressData.progress_percentage,
                    });
                } else {
                    console.warn('[ContentEvt] sendWatchTime: Resposta do backend sem dados de progresso esperados.', response.data);
                }
            } catch (error) {
                console.error('[ContentEvt] sendWatchTime: Erro ao enviar tempo assistido:', error.response || error.message || error);
            }
        },

        formatTime(totalSeconds) {
            const hours = Math.floor(totalSeconds / 3600);
            const minutes = Math.floor((totalSeconds % 3600) / 60);
            const seconds = totalSeconds % 60;

            return [
                hours.toString().padStart(2, '0'),
                minutes.toString().padStart(2, '0'),
                seconds.toString().padStart(2, '0'),
            ].join(':');
        },

        async fetchInitialWatchTime() {
            try {
                const response = await window.axios.get(`/api/evt-progress/watch-time/${this.lessonId}`);
                if (response.data && typeof response.data.watch_time_seconds === 'number') {
                    this.watchTimeSeconds = response.data.watch_time_seconds;
                } else if (response.data && response.data.data && typeof response.data.data.watch_time_seconds === 'number') {
                    this.watchTimeSeconds = response.data.data.watch_time_seconds;
                } else {
                    this.watchTimeSeconds = 0;
                    console.warn('[ContentEvt] fetchInitialWatchTime: Tempo não encontrado ou em formato inesperado. Definido como 0.', response.data);
                }
            } catch (error) {
                this.watchTimeSeconds = 0;
                console.error('[ContentEvt] fetchInitialWatchTime: Erro ao buscar tempo inicial. Definido como 0.', error.response || error.message || error);
            }
        },
    },

    watch: {
        'video.settings.id': {
            handler(newId, oldId) {
                if (newId !== oldId) {
                    if (this.isTracking) {
                        this.stopTracking();
                    }
                    this.watchTimeSeconds = 0;
                    this.playerReadyNotified = false;
                    this.isPlaying = false;
                }
                this.subscribe();
                if (this.url && this.isPageVisible) {
                    // URL existe e página está visível - player recarregará
                }
            },
            immediate: false,
        },
        url(newUrl, oldUrl) {
            if (newUrl && oldUrl && newUrl !== oldUrl) {
                this.playerReadyNotified = false;
                this.isPlaying = false;
            }
        },
        modelValue: {
            handler(newValue) {
                if (newValue && typeof newValue.time === 'number') {
                    if (this.isMountedComponent &&
                        (this.watchTimeSeconds === 0 || this.watchTimeSeconds < newValue.time)) {
                        this.watchTimeSeconds = newValue.time;
                    } else if (this.isMountedComponent && this.watchTimeSeconds > newValue.time) {
                        console.warn(`[ContentEvt] watch modelValue: Tempo local (${this.watchTimeSeconds}s) > newValue.time (${newValue.time}s). Não retrocedendo.`);
                    }
                } else if (this.isMountedComponent && this.watchTimeSeconds !== 0) {
                    // newValue.time indisponível mas há tempo local acumulado
                }
            },
            deep: true,
        },
    },

    async mounted() {
        await this.fetchInitialWatchTime();
        this.subscribe();
        document.addEventListener('visibilitychange', this.handleVisibilityChange);
        this.isMountedComponent = true;
    },

    beforeUnmount() {
        this.stopTracking();
        document.removeEventListener('visibilitychange', this.handleVisibilityChange);
        if (this.video?.settings?.id) {
            window.Echo.leave(`${NCH_ENV.APP_HOST}.Lesson.Live.${this.video.settings.id}`);
        }
        this.isMountedComponent = false;
    },
};
</script>

<style>
.video-player-container {
    min-height: 300px;
    width: 100%;
    background-color: #f0f0f0;
}
.badge-info {
    background-color: #17a2b8;
    color: white;
    padding: 5px 10px;
    border-radius: 4px;
    font-size: 12px;
}
</style>
